import styled from 'styled-components';
import { CSSVars } from '@/styles/styles.config';

/* ═══════════════════════════════════════════
   1. SPLIT HERO
   ═══════════════════════════════════════════ */

export const StyledSplitHero = styled.section`
  background: linear-gradient(165deg, #09093a 0%, ${CSSVars.black} 100%);
  color: ${CSSVars.white};
  padding: 120px 20px 60px;
  overflow: hidden;

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    padding: 140px 30px 80px;
  }
  @media screen and (min-width: ${CSSVars.breakpointLG}) {
    padding: 160px 40px 80px;
  }
`;

export const StyledHeroInner = styled.div`
  max-width: 1120px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 48px;
  align-items: center;

  @media screen and (min-width: ${CSSVars.breakpointLG}) {
    flex-direction: row;
    gap: 64px;
    align-items: stretch;
  }
`;

export const StyledHeroLeft = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;

  h1 {
    font-size: 1.875rem;
    font-weight: 800;
    line-height: 1.1;
    margin-bottom: 20px;
    color: ${CSSVars.white};

    .highlight {
      background: linear-gradient(270deg, var(--accent, ${CSSVars.pink300}) 0%, #fff 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
  }

  .hero-sub {
    font-size: ${CSSVars.fontSizeMD};
    color: rgba(255, 255, 255, 0.75);
    line-height: 1.7;
    font-weight: 300;
    max-width: 520px;
    margin-bottom: 32px;
  }

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    h1 { font-size: ${CSSVars.fontSize2XL}; }
    .hero-sub { font-size: ${CSSVars.fontSizeLG}; }
  }
  @media screen and (min-width: ${CSSVars.breakpointLG}) {
    h1 { font-size: ${CSSVars.fontSize3XL}; }
  }
`;

export const StyledHeroCtas = styled.div`
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 28px;

  .cta-primary {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 14px 28px;
    background: var(--accent, ${CSSVars.blue500});
    color: ${CSSVars.white};
    border-radius: 8px;
    font-weight: 700;
    font-size: ${CSSVars.fontSizeMD};
    text-decoration: none;
    transition: filter 0.2s, transform 0.2s;
    box-shadow: 0 4px 14px rgba(76, 79, 255, 0.35);
    &:hover { filter: brightness(1.15); transform: translateY(-1px); }
  }

  .cta-secondary {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 14px 28px;
    background: transparent;
    color: ${CSSVars.white};
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 8px;
    font-weight: 700;
    font-size: ${CSSVars.fontSizeMD};
    text-decoration: none;
    transition: background 0.2s, border-color 0.2s;
    &:hover { background: rgba(255, 255, 255, 0.08); border-color: rgba(255, 255, 255, 0.5); }
  }
`;

export const StyledTrustBadges = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;

  span {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-size: ${CSSVars.fontSizeXS};
    font-weight: 600;
    color: rgba(255, 255, 255, 0.7);
    white-space: nowrap;

    &::before {
      content: '\\2713';
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 18px;
      height: 18px;
      background: #00b67a;
      border-radius: 50%;
      color: #fff;
      font-size: 10px;
      flex-shrink: 0;
    }
  }
`;

export const StyledHeroRight = styled.div`
  width: 100%;
  max-width: 480px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;

  @media screen and (min-width: ${CSSVars.breakpointLG}) {
    max-width: 440px;
  }
`;

export const StyledBrowserMockup = styled.div`
  background: ${CSSVars.white};
  border-radius: 16px;
  box-shadow: 0 12px 48px rgba(76, 79, 255, 0.15), 0 0 0 1px rgba(76, 79, 255, 0.08);
  overflow: hidden;
  flex: 1;
  display: flex;
  flex-direction: column;

  .browser-bar {
    background: #f1f3f9;
    padding: 10px 16px;
    display: flex;
    align-items: center;
    gap: 8px;
    border-bottom: 1px solid #e0e7ff;
  }

  .b-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
  }

  .browser-url {
    flex: 1;
    background: ${CSSVars.white};
    border-radius: 6px;
    padding: 5px 12px;
    font-size: 11px;
    color: #9ca3af;
    margin: 0 8px;
    border: 1px solid #e5e7eb;
  }

  .browser-content {
    padding: 16px;
    background: ${CSSVars.gray50};
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
    justify-content: center;
  }

  .mock-banner {
    background: var(--accent, ${CSSVars.blue500});
    color: ${CSSVars.white};
    padding: 14px 16px;
    border-radius: 10px;
    text-align: center;
    h3 { font-size: 13px; font-weight: 800; margin-bottom: 3px; }
    p { font-size: 10px; opacity: 0.85; margin-bottom: 8px; }
    .mock-cta { display: inline-block; background: #fff; color: var(--accent, ${CSSVars.blue500}); font-size: 10px; font-weight: 700; padding: 5px 14px; border-radius: 5px; }
  }

  .mock-widgets {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }

  .mock-widget {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 10px;
    .label { font-size: 9px; font-weight: 700; color: var(--accent, ${CSSVars.blue500}); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 5px; }
    .mini-row { display: flex; justify-content: space-between; padding: 3px 0; font-size: 9px; color: #6b7280; border-bottom: 1px solid #f3f4f6; &:last-child { border-bottom: none; } }
    .mini-stars { font-size: 10px; color: #f59e0b; }
    .mini-text { font-size: 9px; color: #6b7280; margin-top: 3px; }
  }

  .mock-notif {
    background: #ff6b35;
    color: #fff;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 10px;
    font-weight: 600;
    display: flex;
    justify-content: space-between;
    align-items: center;
    .notif-cta { background: rgba(255,255,255,0.25); padding: 2px 8px; border-radius: 4px; font-size: 9px; }
  }
`;

export const StyledHeroFloat = styled.div`
  position: relative;
  margin-top: -20px;
  margin-left: auto;
  margin-right: -8px;
  width: fit-content;
  background: #fff;
  border-radius: 12px;
  padding: 12px 16px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  gap: 10px;
  z-index: 2;

  .float-icon {
    width: 32px;
    height: 32px;
    background: #dcfce7;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
  }

  .float-label { font-size: 10px; color: #9ca3af; font-weight: 500; }
  .float-value { font-size: 12px; font-weight: 700; color: ${CSSVars.black}; }
`;

/* ═══════════════════════════════════════════
   2. PLATFORM CAROUSEL
   ═══════════════════════════════════════════ */

export const StyledPlatformSection = styled.div`
  padding: 24px 20px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.07);
  background: ${CSSVars.white};
`;

export const StyledPlatformCarousel = styled.div`
  overflow: hidden;
  position: relative;

  &::before, &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 80px;
    z-index: 1;
    pointer-events: none;
  }
  &::before { left: 0; background: linear-gradient(90deg, ${CSSVars.white} 0%, transparent 100%); }
  &::after { right: 0; background: linear-gradient(270deg, ${CSSVars.white} 0%, transparent 100%); }

  .carousel-track {
    display: flex;
    align-items: center;
    gap: 48px;
    width: max-content;
    animation: scrollLogos 30s linear infinite;
  }

  .logo-item {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    opacity: 0.55;
    transition: opacity 0.3s;
    &:hover { opacity: 1; }
    img { height: 36px; width: auto; object-fit: contain; }
  }

  @keyframes scrollLogos {
    0% { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }
`;

/* ═══════════════════════════════════════════
   3. SHARED SECTION PRIMITIVES
   ═══════════════════════════════════════════ */

export const StyledSection = styled.section<{ $bg?: string }>`
  padding: 64px 20px;
  background: ${({ $bg }) => $bg || CSSVars.white};

  @media screen and (min-width: ${CSSVars.breakpointMD}) { padding: 80px 30px; }
  @media screen and (min-width: ${CSSVars.breakpointLG}) { padding: 96px 40px; }
`;

export const StyledInner = styled.div`
  max-width: 1120px;
  margin: 0 auto;
`;

export const StyledEyebrow = styled.p`
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--accent, ${CSSVars.blue500});
  margin-bottom: 12px;
`;

export const StyledSectionTitle = styled.h2<{ $light?: boolean }>`
  font-size: 1.5rem;
  font-weight: 800;
  line-height: 1.15;
  color: ${({ $light }) => $light ? CSSVars.white : CSSVars.black};
  margin-bottom: 16px;

  @media screen and (min-width: ${CSSVars.breakpointMD}) { font-size: ${CSSVars.fontSize2XL}; }
  @media screen and (min-width: ${CSSVars.breakpointLG}) { font-size: 2.625rem; }
`;

export const StyledSectionSub = styled.p<{ $light?: boolean; $center?: boolean }>`
  font-size: ${CSSVars.fontSizeMD};
  color: ${({ $light }) => $light ? 'rgba(255,255,255,0.7)' : CSSVars.gray600};
  line-height: 1.7;
  font-weight: 300;
  max-width: 600px;
  margin-top: 8px;
  ${({ $center }) => $center && 'margin-left: auto; margin-right: auto;'}

  @media screen and (min-width: ${CSSVars.breakpointMD}) { font-size: 17px; }
`;

/* ═══════════════════════════════════════════
   4. INDUSTRY STATS GRID
   ═══════════════════════════════════════════ */

export const StyledStatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1px;
  background: rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 16px;
  overflow: hidden;
  margin-top: 40px;

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const StyledStatCell = styled.div`
  background: ${CSSVars.white};
  padding: 32px 28px;
  text-align: center;

  .stat-value {
    font-size: 2.5rem;
    font-weight: 900;
    color: var(--accent, ${CSSVars.blue500});
    line-height: 1;
  }

  .stat-label {
    font-size: ${CSSVars.fontSizeSM};
    color: ${CSSVars.gray600};
    margin-top: 8px;
    font-weight: 400;
  }

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    padding: 40px 32px;
    .stat-value { font-size: 2.75rem; }
  }
`;

export const StyledSourcesNote = styled.p`
  font-size: ${CSSVars.fontSizeXS};
  color: ${CSSVars.gray400};
  margin-top: 16px;
  font-weight: 300;
  font-style: italic;
  text-align: center;
`;

/* ═══════════════════════════════════════════
   5. PAIN POINTS COMPARISON TABLE
   ═══════════════════════════════════════════ */

export const StyledComparisonTable = styled.div`
  margin-top: 40px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 16px;
  overflow: hidden;

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
`;

export const StyledComparisonCol = styled.div<{ $type: 'pain' | 'fix' }>`
  .col-header {
    padding: 14px 20px;
    font-size: 13px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    background: ${({ $type }) => $type === 'pain' ? '#fff1f0' : '#ecfdf5'};
    color: ${({ $type }) => $type === 'pain' ? '#dc2626' : '#059669'};
  }

  .col-items {
    display: flex;
    flex-direction: column;
  }
`;

export const StyledComparisonRow = styled.div<{ $type: 'pain' | 'fix' }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  font-size: ${CSSVars.fontSizeSM};
  color: ${CSSVars.gray600};
  line-height: 1.5;
  background: ${({ $type }) => $type === 'fix' ? '#f9fffe' : CSSVars.white};

  .row-icon {
    flex-shrink: 0;
    font-size: 14px;
    margin-top: 1px;
  }
`;

/* ═══════════════════════════════════════════
   6. WIDGET SHOWCASE
   ═══════════════════════════════════════════ */

export const StyledShowcaseGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  margin-top: 44px;

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

export const StyledShowcaseCard = styled.div`
  background: ${CSSVars.white};
  border: 1.5px solid rgba(0, 0, 0, 0.08);
  border-radius: 16px;
  overflow: hidden;
  transition: border-color 0.25s, box-shadow 0.25s, transform 0.25s;

  &:hover {
    border-color: var(--accent, ${CSSVars.blue500});
    box-shadow: 0 8px 32px var(--accent-light, rgba(76, 79, 255, 0.1));
    transform: translateY(-3px);
  }

  .card-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 10px;

    .widget-logo {
      width: 40px;
      height: 40px;
      border-radius: 10px;
      object-fit: contain;
      flex-shrink: 0;
    }

    .widget-logo-placeholder {
      width: 40px;
      height: 40px;
      border-radius: 10px;
      background: var(--accent-light, rgba(76, 79, 255, 0.08));
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 20px;
      flex-shrink: 0;
    }
  }

  .card-preview {
    height: 175px;
    background: #f8faff;
    display: flex;
    align-items: center;
    justify-content: center;
    border-bottom: 1.5px solid rgba(0, 0, 0, 0.06);
    padding: 18px;
    position: relative;
    overflow: hidden;
  }

  .card-body {
    padding: 16px 16px;
    @media screen and (min-width: ${CSSVars.breakpointMD}) { padding: 20px 22px; }
  }

  .card-name {
    font-size: 17px;
    font-weight: 800;
    color: ${CSSVars.black};
    margin-bottom: 6px;
  }

  .card-desc {
    font-size: 13px;
    color: ${CSSVars.gray600};
    line-height: 1.6;
    margin-bottom: 14px;
    font-weight: 300;
  }

  .card-link {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 13px;
    font-weight: 700;
    color: var(--accent, ${CSSVars.blue500});
    text-decoration: none;
    margin-top: 12px;
    &:hover { text-decoration: underline; }
  }
`;

export const StyledUseCaseBadge = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 12px;
  color: #166534;
  line-height: 1.5;

  strong { font-weight: 700; }
`;

export const StyledShowcaseFooter = styled.div`
  text-align: center;
  margin-top: 40px;

  .footer-note {
    font-size: ${CSSVars.fontSizeSM};
    color: ${CSSVars.gray500};
    margin-bottom: 16px;
  }

  a {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 14px 28px;
    background: var(--accent, ${CSSVars.blue500});
    color: ${CSSVars.white};
    border-radius: 8px;
    font-weight: 700;
    font-size: 15px;
    text-decoration: none;
    transition: filter 0.2s, transform 0.2s;
    box-shadow: 0 4px 14px rgba(76, 79, 255, 0.25);
    &:hover { filter: brightness(1.15); transform: translateY(-1px); }
  }
`;

/* ═══════════════════════════════════════════
   7. INDUSTRY NARRATIVE (long-form content)
   ═══════════════════════════════════════════ */

export const StyledNarrative = styled.div`
  max-width: 780px;
  margin-top: 36px;

  p {
    font-size: 15px;
    color: ${CSSVars.gray700};
    line-height: 1.85;
    font-weight: 300;
    margin-bottom: 18px;

    &:last-child { margin-bottom: 0; }

    strong {
      font-weight: 600;
      color: ${CSSVars.black};
    }
  }
`;

/* ═══════════════════════════════════════════
   8. INDUSTRY INSIGHTS (fun-fact callout cards)
   ═══════════════════════════════════════════ */

export const StyledInsightsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  margin-top: 40px;

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media screen and (min-width: ${CSSVars.breakpointLG}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const StyledInsightCard = styled.div`
  background: ${CSSVars.white};
  border: 1.5px solid rgba(0, 0, 0, 0.08);
  border-radius: 14px;
  padding: 28px 24px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
  transition: border-color 0.2s, transform 0.2s;
  text-align: center;

  &:hover {
    border-color: var(--accent-border, rgba(76, 79, 255, 0.3));
    transform: translateY(-2px);
  }

  .insight-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: var(--accent-light, rgba(76, 79, 255, 0.08));
    color: var(--accent, ${CSSVars.blue500});
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 14px;

    svg { width: 24px; height: 24px; }
  }

  .insight-stat {
    font-size: 1.5rem;
    font-weight: 900;
    color: var(--accent, ${CSSVars.blue500});
    margin-bottom: 4px;
    line-height: 1.2;
  }

  .insight-headline {
    font-size: ${CSSVars.fontSizeSM};
    font-weight: 700;
    color: ${CSSVars.black};
    margin-bottom: 8px;
  }

  .insight-body {
    font-size: 13px;
    color: ${CSSVars.gray600};
    line-height: 1.65;
    font-weight: 300;
  }

  .insight-source {
    font-size: 11px;
    color: ${CSSVars.gray400};
    margin-top: 10px;
    font-style: italic;
  }
`;

/* ═══════════════════════════════════════════
   9. ROI TABLE
   ═══════════════════════════════════════════ */

export const StyledRoiTable = styled.div`
  margin-top: 32px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 14px;
  overflow: hidden;

  table {
    width: 100%;
    border-collapse: collapse;
  }

  thead tr {
    background: linear-gradient(90deg, #09093a, #0d0e30);
  }

  thead th {
    color: ${CSSVars.gray300};
    padding: 14px 20px;
    font-size: ${CSSVars.fontSizeXS};
    text-transform: uppercase;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-align: left;
  }

  tbody tr {
    background: ${CSSVars.white};
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    &:hover { background: rgba(76, 79, 255, 0.03); }
  }

  tbody td {
    padding: 13px 20px;
    font-size: ${CSSVars.fontSizeSM};
    color: ${CSSVars.gray700};
    font-weight: 300;
  }

  .col-label { font-weight: 600; color: ${CSSVars.black}; }
  .col-highlight { font-weight: 700; color: var(--accent, ${CSSVars.blue500}); }

  tr.row-highlight td {
    background: var(--accent-light, rgba(76, 79, 255, 0.06));
    font-weight: 600;
    color: ${CSSVars.black};
  }
  tr.row-highlight .col-highlight {
    font-weight: 900;
  }
`;

export const StyledRoiNarrative = styled.div`
  margin-top: 24px;
  max-width: 780px;

  p {
    font-size: 15px;
    color: ${CSSVars.gray700};
    line-height: 1.85;
    font-weight: 300;
    margin-bottom: 14px;

    &:last-child { margin-bottom: 0; }

    strong {
      font-weight: 700;
      color: var(--accent, ${CSSVars.blue500});
    }
  }
`;
